import { z } from 'zod';
// ============================================================
// RSVP Schemas
// ============================================================
export const AttendanceStatusSchema = z.enum([
    'ATTENDING',
    'NOT_ATTENDING',
    'MAYBE',
]);
export const MealOptionSchema = z.enum(['ADULT', 'CHILD', 'VEGETARIAN', 'NONE']);
export const RSVPSchema = z.object({
    id: z.string().cuid2(),
    invitationId: z.string().cuid2(),
    guestName: z.string().max(255),
    guestPhone: z.string().max(500).nullable(),
    guestEmail: z.string().max(500).nullable(),
    attendance: AttendanceStatusSchema,
    guestCount: z.number().int().min(1).max(10).default(1),
    mealOption: MealOptionSchema.nullable(),
    message: z.string().nullable(),
    submittedAt: z.date(),
});
// ============================================================
// 개인정보 수집·이용 동의 (PIPA 제15조)
// ============================================================
/** 현재 RSVP 동의 약관 버전. 약관 변경 시 이 값을 올리면 신규 row에 새 버전이 기록됨. */
export const RSVP_PRIVACY_CONSENT_VERSION = '2026-05-08';
/**
 * RSVP 개인정보 수집·이용 동의 안내문 ([자세히보기] 모달 본문).
 * 클라이언트 컨트롤러와 처리방침 모두 이 상수를 참조해 정합 보장.
 */
export const RSVP_PRIVACY_CONSENT_NOTICE = {
    version: RSVP_PRIVACY_CONSENT_VERSION,
    title: '개인정보 수집·이용 동의',
    items: '성함, (선택) 동행인 성함, 참석여부, 식사여부, 전세버스 탑승여부, 전달사항',
    purpose: '결혼식 참석 여부 확인 및 예식 운영',
    retention: '예식 종료 후 90일 또는 청첩장 만료 시까지',
    refusal: '동의를 거부할 권리가 있으며, 거부 시 참석여부 전달이 제한됩니다.',
};
// ============================================================
// API Request/Response
// ============================================================
export const SubmitRSVPRequestSchema = z.object({
    invitationId: z.string().cuid2(),
    guestName: z
        .string()
        .min(1, '이름을 입력하세요')
        .max(255, '이름은 255자를 초과할 수 없습니다'),
    guestPhone: z
        .string()
        .regex(/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/, '올바른 전화번호를 입력하세요')
        .optional()
        .transform((val) => val?.replace(/-/g, '')),
    guestEmail: z.string().email('올바른 이메일을 입력하세요').optional(),
    attendance: AttendanceStatusSchema,
    guestCount: z
        .number()
        .int()
        .min(1, '최소 1명 이상이어야 합니다')
        .max(10, '최대 10명까지 가능합니다')
        .default(1),
    mealOption: MealOptionSchema.optional(),
    message: z.string().max(500, '메시지는 500자를 초과할 수 없습니다').optional(),
    /** 개인정보 수집·이용 동의 timestamp (ISO 8601). 누락 시 서버가 거부. */
    privacyConsentedAt: z.string().datetime({ message: '개인정보 수집·이용 동의가 필요합니다' }),
    /** 동의한 약관 버전. 누락 시 서버가 현재 버전으로 보강. */
    privacyConsentVersion: z.string().max(32).optional(),
});
export const RSVPResponseSchema = RSVPSchema.omit({
    guestPhone: true,
    guestEmail: true,
}).extend({
    guestPhoneMasked: z.string().optional(),
    guestEmailMasked: z.string().optional(),
});
export const RSVPStatsResponseSchema = z.object({
    total: z.number().int().min(0),
    attending: z.number().int().min(0),
    notAttending: z.number().int().min(0),
    maybe: z.number().int().min(0),
    totalGuests: z.number().int().min(0),
    mealStats: z.object({
        adult: z.number().int().min(0),
        child: z.number().int().min(0),
        vegetarian: z.number().int().min(0),
        none: z.number().int().min(0),
    }),
});
export const RSVPListResponseSchema = z.object({
    rsvps: z.array(RSVPResponseSchema),
    stats: RSVPStatsResponseSchema,
    invitationId: z.string().cuid2(),
});
// ============================================================
// Validation Helpers
// ============================================================
export const maskPhoneNumber = (phone) => {
    const cleaned = phone.replace(/-/g, '');
    if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)}-****-${cleaned.slice(-4)}`;
    }
    return '***-****-****';
};
export const maskEmail = (email) => {
    const [local, domain] = email.split('@');
    if (!local || !domain)
        return '***@***';
    const maskedLocal = local[0] + '***';
    return `${maskedLocal}@${domain}`;
};
export const isValidKoreanPhone = (phone) => {
    const cleaned = phone.replace(/-/g, '');
    return /^01[0-9]{8,9}$/.test(cleaned);
};
//# sourceMappingURL=rsvp.js.map